
import React from 'react';
import { Star } from 'lucide-react';
import { SimulationConfig } from '@/types';
import { useAuth } from '@/context/auth-context';
import { usePayment } from '@/contexts/PaymentContext';
import { CloudStorageSection } from '@/components/settings/CloudStorageSection';

interface UpgradeTabProps {
  config: SimulationConfig;
}

export const UpgradeTab: React.FC<UpgradeTabProps> = ({ config }) => {
  const { user } = useAuth();
  const { subscription } = usePayment();

  const getPlanName = (p: string) => {
    const up = (p || '').toUpperCase();
    if (up.includes('ENTERPRISE')) return 'Enterprise Studio';
    if (up.includes('ELITE')) return 'Studio Elite';
    if (up.includes('PRO')) return 'Pro Creator';
    if (user?.is_beta) return 'Beta Tester (All Unlocked)';
    return 'Starter Studio (Free)';
  };

  const planName = getPlanName(user?.plan || 'FREE');

  return (
    <div className="flex flex-col gap-6 pt-4">
        <div className={`p-10 rounded-[3rem] ${config.darkMode ? 'bg-zinc-950 border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-950'} relative overflow-hidden group shadow-2xl border`}>
            <div className="absolute -inset-0 opacity-5 blur-3xl group-hover:opacity-10 transition-all duration-1000 bg-cyan-500" />
            
            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl ${config.darkMode ? 'bg-white/10' : 'bg-zinc-100'} flex items-center justify-center`}>
                        <Star className="w-5 h-5 text-amber-500" />
                    </div>
                    <h2 className="text-3xl font-black uppercase tracking-tighter">Your Account</h2>
                </div>
                
                <div className={`${config.darkMode ? 'bg-white/5 border-white/10' : 'bg-zinc-50 border-zinc-200 shadow-inner'} p-6 rounded-2xl border mb-8 mt-4`}>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40 mb-2">Current Active Tier</p>
                    <div className="flex items-center justify-between">
                        <h3 className="text-2xl font-black uppercase tracking-tight">
                            {planName}
                        </h3>
                        {user?.is_beta ? (
                            <div className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                                <span className="text-[8px] font-black uppercase tracking-widest text-emerald-400">
                                    Beta Access Active
                                </span>
                            </div>
                        ) : user?.is_trial ? (
                            <div className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
                                <span className="text-[8px] font-black uppercase tracking-widest text-amber-400">
                                    Trial: {Math.ceil((user.access_remaining || 0) / 86400)} Days Left
                                </span>
                            </div>
                        ) : (subscription?.status === 'active' || subscription?.status === 'cancelling') && (
                            <div className={`px-3 py-1 ${subscription.status === 'cancelling' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20'} rounded-full`}>
                                <span className={`text-[8px] font-black uppercase tracking-widest ${subscription.status === 'cancelling' ? 'text-amber-400' : 'text-emerald-400'}`}>
                                    {subscription.status === 'cancelling' ? 'Valid until expiry' : 'Active via PayPal'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mb-8">
                    <CloudStorageSection />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className={`p-4 rounded-2xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100'}`}>
                        <p className="text-[8px] font-black uppercase tracking-widest opacity-30 mb-1">Account ID</p>
                        <p className={`text-[10px] font-mono truncate ${config.darkMode ? 'text-white/60' : 'text-zinc-500'}`}>{user?.id || 'Local Anonymous'}</p>
                    </div>
                    <div className={`p-4 rounded-2xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100'}`}>
                        <p className="text-[8px] font-black uppercase tracking-widest opacity-30 mb-1">Sync Engine</p>
                        <p className="text-[10px] font-black uppercase text-amber-500">{user?.id ? 'Online Sync' : 'Local Only'}</p>
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    <a 
                        href="/plans"
                        className={`inline-flex items-center justify-center w-full py-5 rounded-[1.5rem] text-[11px] font-black uppercase tracking-[0.2em] transition-all active:scale-95 shadow-xl ${
                            config.darkMode 
                            ? 'bg-cyan-500 text-white hover:bg-cyan-400 shadow-cyan-500/20' 
                            : 'bg-neon-cyan text-white hover:bg-cyan-600 shadow-cyan-500/20'
                        }`}
                    >
                        {subscription?.status === 'active' ? 'Manage Subscription' : 'View Upgrade Plans'}
                    </a>
                    <button 
                        onClick={() => window.location.href = '/dashboard'}
                        className={`w-full py-4 text-[10px] font-black uppercase tracking-widest transition-colors ${config.darkMode ? 'text-white/40 hover:text-white' : 'text-zinc-400 hover:text-zinc-950'}`}
                    >
                        Member Dashboard
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};
